import { Check, ChevronDownIcon, Plus, UserPlus } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Highlight } from "@/components/highlight";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SELECT_TRIGGER_CLASS } from "@/components/ui/select";
import {
  type EmailProblem,
  emailProblem,
  matchesPerson,
  type NamedPerson,
  normalEmail,
  personLabel,
} from "@/lib/people";
import { cn } from "@/lib/utils";

interface AddByEmail {
  /** The domains "Toevoegen via e-mail" accepts; none means any full address. */
  domains: readonly string[];
}

interface PersonPickerProps {
  /** Everyone the page knows, by name or email. */
  people: readonly NamedPerson[];
  /** What the button says: "Mensen toevoegen". */
  label: string;
  /**
   * Called with the email picked, lower case. In `multiple` mode `on` says
   * whether it was ticked or unticked, and the list stays open; otherwise
   * every pick is `on` and closes it.
   */
  onPick: (email: string, on: boolean) => void;
  /** Tick several people in one go: `chosen` are ticked. */
  multiple?: boolean;
  chosen?: readonly string[];
  /**
   * A last option, "Toevoegen via e-mail", that adds the search text as an
   * address, for someone the page does not know yet. Left out, the picker
   * offers the people it lists and no one else.
   */
  addByEmail?: AddByEmail;
  className?: string;
}

/**
 * A searchable list of the people the page knows, in the dashboard's theme: a
 * button like `<SelectTrigger>` opens a search box over the list, name first
 * and email muted under it. Typing filters by name or email. With
 * `addByEmail`, "Toevoegen via e-mail" adds the search text as an address
 * when it is a full one in an allowed domain, and says why not otherwise; it
 * closes the list unless several people can be ticked.
 * Keyboard: the arrows move, Enter picks, Escape closes.
 */
export function PersonPicker({
  people,
  label,
  onPick,
  multiple = false,
  chosen = [],
  addByEmail,
  className,
}: PersonPickerProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [problem, setProblem] = useState<EmailProblem | null>(null);

  const ticked = new Set(chosen);
  const known = new Set(people.map((person) => normalEmail(person.email)));
  // Someone added by email, or no longer listed, is not among the people: list them too, so they can be unticked.
  const extra = chosen
    .filter((email) => !known.has(normalEmail(email)))
    .map((email): NamedPerson => ({ email, name: null }));
  const options = [...people, ...extra].filter((person) => matchesPerson(person, query));
  // The add-by-email option, when there is one, comes after the people.
  const addIndex = addByEmail ? options.length : null;
  const count = options.length + (addByEmail ? 1 : 0);
  const current = count > 0 ? Math.min(active, count - 1) : null;
  const optionId = (index: number) => `${listId}-${index}`;

  // Keep the option the arrows reached in view.
  useEffect(() => {
    if (open) {
      document.getElementById(`${listId}-${active}`)?.scrollIntoView({ block: "nearest" });
    }
  }, [active, open, listId]);

  const changeOpen = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setActive(0);
      setProblem(null);
    }
  };

  const pick = (email: string) => {
    const on = !(multiple && ticked.has(email));
    onPick(email, on);
    if (!multiple) {
      changeOpen(false);
    }
  };

  const addTyped = () => {
    if (!addByEmail) {
      return;
    }
    const found = emailProblem(query, addByEmail.domains);
    setProblem(found);
    if (found) {
      return;
    }
    const email = normalEmail(query);
    if (!ticked.has(email)) {
      onPick(email, true);
    }
    if (multiple) {
      setQuery("");
      setActive(0);
    } else {
      changeOpen(false);
    }
  };

  const choose = (index: number) => {
    const person = options[index];
    if (person) {
      pick(person.email);
    } else if (index === addIndex) {
      addTyped();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown" && count > 0) {
      event.preventDefault();
      setActive((index) => (index + 1) % count);
    } else if (event.key === "ArrowUp" && count > 0) {
      event.preventDefault();
      setActive((index) => (index - 1 + count) % count);
    } else if (event.key === "Enter") {
      event.preventDefault();
      if (current !== null) {
        choose(current);
      }
    }
  };

  const domainList = (addByEmail?.domains ?? []).map((domain) => `@${domain}`).join(", ");

  return (
    <Popover open={open} onOpenChange={changeOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-size="sm" className={cn(SELECT_TRIGGER_CLASS, className)}>
          <span className="flex min-w-0 items-center gap-2">
            <Plus aria-hidden="true" className="text-muted size-4" />
            <span className="truncate">{label}</span>
          </span>
          <ChevronDownIcon aria-hidden="true" className="text-muted size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 max-w-[calc(100vw-2rem)] p-0">
        <div className="border-b p-2">
          <Input
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={current === null ? undefined : optionId(current)}
            aria-label={t("picker.search")}
            placeholder={t("picker.search")}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setActive(0);
              setProblem(null);
            }}
            onKeyDown={onKeyDown}
            className="h-9 pointer-coarse:h-10"
          />
        </div>
        <ul
          id={listId}
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          className="scrollbar-themed max-h-64 overflow-y-auto p-1"
        >
          {options.map((person, index) => {
            const on = ticked.has(person.email);
            return (
              <li
                key={person.email}
                id={optionId(index)}
                role="option"
                aria-selected={multiple ? on : index === current}
                // Keep the focus in the search box, so typing goes on after a click.
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => pick(person.email)}
                onMouseMove={() => setActive(index)}
                className={cn(
                  "flex cursor-default items-center gap-2 rounded-sm px-2 py-1.5 text-sm pointer-coarse:py-2.5",
                  index === current && "bg-accent-light",
                )}
              >
                {multiple && (
                  <span
                    aria-hidden="true"
                    className={cn(
                      "border-input flex size-4 shrink-0 items-center justify-center rounded-sm border",
                      on && "bg-accent text-accent-foreground border-accent",
                    )}
                  >
                    {on && <Check className="size-3.5" />}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="text-foreground block truncate font-medium">
                    <Highlight text={personLabel(person)} query={query} />
                  </span>
                  {person.name?.trim() && (
                    <span className="text-muted block truncate text-xs">
                      <Highlight text={person.email} query={query} />
                    </span>
                  )}
                </span>
              </li>
            );
          })}
          {options.length === 0 && (
            <li role="presentation" className="text-muted px-2 py-1.5 text-sm">
              {t("picker.empty")}
            </li>
          )}
          {addIndex !== null && (
            <li
              id={optionId(addIndex)}
              role="option"
              aria-selected={current === addIndex}
              onMouseDown={(event) => event.preventDefault()}
              onClick={addTyped}
              onMouseMove={() => setActive(addIndex)}
              className={cn(
                "bg-card sticky bottom-0 mt-1 flex cursor-default items-center gap-2 rounded-sm border-t px-2 py-2 text-sm pointer-coarse:py-3",
                current === addIndex && "bg-accent-light",
              )}
            >
              <UserPlus aria-hidden="true" className="text-primary size-4 shrink-0" />
              <span className="min-w-0">
                <span className="text-primary block font-medium">{t("picker.addByEmail")}</span>
                {query.trim() && (
                  <span className="text-muted block truncate text-xs">{normalEmail(query)}</span>
                )}
              </span>
            </li>
          )}
        </ul>
        {problem && (
          <p role="alert" className="text-destructive border-t px-3 py-2 text-xs">
            {problem === "domain"
              ? t("picker.domainNotAllowed", { domains: domainList })
              : t("picker.invalidEmail")}
          </p>
        )}
      </PopoverContent>
    </Popover>
  );
}
